import { useState, useEffect, useCallback } from 'react';
import { fetchWater, logWater } from '../api';

const QUICK_ADD = [
  { label: '+250ml', ml: 250 },
  { label: '+500ml', ml: 500 },
  { label: '+1L',    ml: 1000 },
];

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export default function WaterTracker({ date, goalMl = 2500 }) {
  const [totalMl,  setTotalMl]  = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [adding,   setAdding]   = useState(false);
  const [customMl, setCustomMl] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const loadWater = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchWater(date);
      setTotalMl(data.totalMl || 0);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [date]);

  useEffect(() => { loadWater(); }, [loadWater]);

  async function handleAdd(ml) {
    setAdding(true);
    try {
      const res = await logWater(date, ml);
      setTotalMl(res.totalMl);
    } catch (e) { console.error(e); }
    setAdding(false);
  }

  async function handleCustomAdd() {
    const ml = parseFloat(customMl);
    if (!ml || ml <= 0) return;
    await handleAdd(ml);
    setCustomMl('');
    setShowCustom(false);
  }

  const pct       = Math.min((totalMl / goalMl) * 100, 100);
  const remaining = Math.max(0, goalMl - totalMl);
  const glasses   = Math.round(totalMl / 250); // 1 glass = 250ml

  // Color changes as you drink more
  const barColor = pct >= 100
    ? 'linear-gradient(90deg, #34d399, #10b981)'
    : pct >= 60
    ? 'linear-gradient(90deg, #60a5fa, #3b82f6)'
    : 'linear-gradient(90deg, #93c5fd, #60a5fa)';

  return (
    <div style={{
      background: 'rgba(96,165,250,0.08)',
      border: '1px solid rgba(96,165,250,0.2)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: 16, padding: 20, marginBottom: 16,
    }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 20 }}>💧</span>
          <span style={{ fontWeight: 700, fontSize: 15 }}>Water Intake</span>
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>
          {glasses} glasses
        </div>
      </div>

      {/* Progress */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          fontSize: 13, marginBottom: 6 }}>
          <span style={{ fontWeight: 700, color: '#60a5fa' }}>
            {totalMl >= 1000
              ? `${(totalMl/1000).toFixed(1)}L`
              : `${Math.round(totalMl)}ml`}
          </span>
          <span style={{ color: 'rgba(255,255,255,0.4)' }}>
            {remaining >= 1000
              ? `${(remaining/1000).toFixed(1)}L remaining`
              : `${Math.round(remaining)}ml remaining`}
            {' / '}
            {goalMl >= 1000 ? `${goalMl/1000}L` : `${goalMl}ml`} goal
          </span>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.1)',
          borderRadius: 999, height: 10, overflow: 'hidden' }}>
          <div style={{
            width: `${pct}%`, height: 10, borderRadius: 999,
            background: barColor,
            boxShadow: '0 0 10px rgba(96,165,250,0.4)',
            transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
          }} />
        </div>
        {pct >= 100 && (
          <div style={{ fontSize: 12, color: '#34d399',
            marginTop: 6, textAlign: 'center' }}>
            🎉 Daily water goal reached!
          </div>
        )}
      </div>

      {/* Quick add buttons */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {QUICK_ADD.map(({ label, ml }) => (
          <button
            key={label}
            onClick={() => handleAdd(ml)}
            disabled={adding || loading}
            style={{
              flex: 1, padding: '10px 8px', borderRadius: 10,
              border: '1px solid rgba(96,165,250,0.3)',
              background: 'rgba(96,165,250,0.12)',
              color: '#93c5fd', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s',
              opacity: adding ? 0.6 : 1,
            }}>
            {adding ? '...' : label}
          </button>
        ))}
        <button
          onClick={() => setShowCustom(!showCustom)}
          style={{
            flex: 1, padding: '10px 8px', borderRadius: 10,
            border: '1px solid rgba(255,255,255,0.15)',
            background: 'rgba(255,255,255,0.06)',
            color: 'rgba(255,255,255,0.5)', fontSize: 14,
            cursor: 'pointer',
          }}>
          Custom
        </button>
      </div>

      {/* Custom amount input */}
      {showCustom && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          <input
            type="number"
            placeholder="Enter ml (e.g. 350)"
            value={customMl}
            onChange={e => setCustomMl(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCustomAdd()}
            style={{
              flex: 1, padding: '10px 14px', borderRadius: 10,
              border: '1px solid rgba(96,165,250,0.3)',
              background: 'rgba(255,255,255,0.07)',
              color: '#fff', fontSize: 14, outline: 'none',
            }}
          />
          <button onClick={handleCustomAdd} disabled={adding}
            style={{
              padding: '10px 16px', borderRadius: 10,
              background: 'linear-gradient(135deg, #60a5fa, #3b82f6)',
              border: 'none', color: '#fff', fontWeight: 700,
              cursor: 'pointer', fontSize: 14,
            }}>
            Add
          </button>
        </div>
      )}
    </div>
  );
}